import { useState, useRef, useEffect } from "react";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Input, { type InputRef } from "../../components/core/input.core";
import Button from "../../components/core/button.core";
import { useUpdateProfileHttp } from "../../hooks/http/users/update-profile.hook";
import { useNavigate } from "react-router-dom";
import { useUserStore } from "../../../store/user.store";
import { useToast } from "../../../hooks/use-toast";

// Define the form schema with Zod
const nameSchema = z.object({
  name: z
    .string()
    .min(2, { message: "Please enter a valid name (minimum 2 characters)" }),
});

// Infer the type from the schema
type NameFormValues = z.infer<typeof nameSchema>;

export default function NameForm() {
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const nameRef = useRef<InputRef>(null);
  const navigate = useNavigate();
  const { setUser, user } = useUserStore();
  const { toast } = useToast();

  const { execute: updateProfileHttp, fetching: updatingProfile } =
    useUpdateProfileHttp();

  const nameForm = useForm<NameFormValues>({
    resolver: zodResolver(nameSchema),
    defaultValues: {
      name: "",
    },
  });

  // Add form error handling
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (nameForm.formState.errors) {
      const key = Object.keys(nameForm.formState.errors)[0];
      if (key) {
        const error =
          nameForm.formState.errors[
            key as keyof typeof nameForm.formState.errors
          ];
        const message = error?.message || "An error occurred";

        toast({
          content: message,
          toastType: "error",
        });
      }
    }
  }, [nameForm.formState.errors]);

  const onSubmitName = nameForm.handleSubmit(async (data) => {
    const response = await updateProfileHttp({
      body: {
        name: data.name,
      },
    });

    if (!response.success) {
      setFormErrors([response.error.message || "Failed to update profile"]);
      return;
    }

    // Update the user in auth context
    setUser(response.data.user);

    // Navigate to home page after successful profile update
    navigate("/");
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (user?.name) {
      navigate("/");
    }
  }, [user?.name]);

  return (
    <div className="flex flex-col items-center gap-10 bg-white px-5 pt-10 w-full h-screen sm:h-fit">
      <form
        onSubmit={onSubmitName}
        className="flex flex-col justify-between w-full max-w-md h-full"
      >
        <div className="flex flex-col w-full">
          <h1 className="pb-10 font-cabinetgrotesk font-medium text-3xl tracking-tight">
            What's your name?
          </h1>

          {formErrors.length > 0 && (
            <div className="bg-red-100 mb-4 px-4 py-3 border border-red-400 rounded text-red-700">
              {formErrors.map((error, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                <p key={index}>{error}</p>
              ))}
            </div>
          )}

          <Controller
            control={nameForm.control}
            name="name"
            render={({ field, fieldState }) => (
              <Input
                ref={nameRef}
                label="Your name"
                placeholder="Enter your name"
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                className="py-4"
                type="text"
              />
            )}
          />
        </div>

        <div className="justify-center items-center pt-8 pb-4 w-full">
          <Button
            className="w-full font-semibold font-sm"
            label="Continue"
            type="button"
            onClick={onSubmitName}
            isLoading={updatingProfile}
          />
        </div>
      </form>
    </div>
  );
}
